import Chat from "./Chat.jsx"
import useMessages from './useMessages.jsx';
import { useEffect, useState, useRef, createContext, useContext } from "react";
import { Timestamp } from "firebase/firestore";
import { MSG_TYPE_ENUM } from './Message';
import './visualizer.css';
const { AUTO_SOLICIT, AUTO_SOLICIT_SAVED, AUTO_SOLICIT_CLOSED, EMAIL_VISITOR, TEXT_VISITOR, FILE_VISITOR } = MSG_TYPE_ENUM;

const VisualizerContext = createContext();

const DEFAULT_CONFIG = {
    default: true,
    workspace: "T03174J6D3K",
    workspaceType: 0,
    workspaceStyles: {
        colors: {
            name: "Rainforest Canopy",
            mainColor: "#27ca52",
            V1: "linear-gradient(-45deg, #1B5E20, #00C853)", /* Chat Launcher */
            V2: "linear-gradient(0deg, #1F7D5A, #27CA52)", /* Chat Header */
            V3: "linear-gradient(-5deg, #1B5E20, #00C853)", /* Visitor Message */
            V4: "linear-gradient(0deg, #1B5E20, #3DBE6C)", /* Chat Footer */
            V5: "linear-gradient(#27ca52, #10A838)",
        },
        customIcon: null,
        iconSetting1: {
            iconSetting: 'choose',
            selectedIcon: 'default',
        },
        iconSetting2: {
            iconSetting: 'choose',
            selectedIcon: 'default',
        },
        iconSetting3: {
            iconSetting: 'choose',
            selectedIcon: 'default',
        },
        iconSetting4: {
            iconSetting: 'choose',
            selectedIcon: 'default',
        },
    },
    bannedIPs: [],
    chatTitle: 'PageParrot Support',
    welcomeMsg: 'Welcome to our chat! How can I help you today?',
    newMessageTitle: 'New Message!',
    contactSetting: false,
    businessHourSetting: 1,
    showOutsideHours: false,
    offlineWelcomeMsg: 'We are currently offline. Please leave a message and we will get back to you as soon as possible.',
    timezone: null,
    businessHours: {
        open: 9,
        close: 17,
        Monday: { isOpen: true, open: 9, close: 17 },
        Tuesday: { isOpen: true, open: 9, close: 17 },
        Wednesday: { isOpen: true, open: 9, close: 17 },
        Thursday: { isOpen: true, open: 9, close: 17 },
        Friday: { isOpen: true, open: 9, close: 17 },
        Saturday: { isOpen: false, open: 9, close: 17 },
        Sunday: { isOpen: false, open: 9, close: 17 },
    },
    allowedFileTypes: ['.jpg', '.png', '.gif', '.pdf'],
    autoEngageDelay: 10,
    autoEngages: [
        {
            message: "Welcome! How can we help you today?",
            path: "",
        },
    ],
    autoReplies: [
        {
            option: "Hello",
            reply: "Hello! How can I help you today?",
        },
    ],
    contactModuleSettings: {
        title: 'Contact Us',
        displayEmail: 'support@pageparrot.com',
        inboxEmail: 'support@pageparrot.com',
        phoneNumber: '',
        address: '',
        displayEmailEnabled: true,
        phoneNumberEnabled: false,
        addressEnabled: false,
        enableYouGotMailMessage: true,
        enableRecaptcha: false,
        recaptchaPublicKey: '',
    },
    icon: null,
    subscription: {
        addons: [{ enabled: true }, { enabled: true }, { enabled: true }, { enabled: false }],
    },
}

export default function Visualizer({ activeConfig, requestedVisualTrigger, requestedVisualIndex, currentUser }) {
    const prevWorkspaceStyleName = useRef(null);
    if (activeConfig === null) {
        activeConfig = DEFAULT_CONFIG;
    }

    const prevRequestedVisualTrigger = useRef(null);
    const [activeVisualTrigger, setActiveVisualTrigger] = useState(null);

    useEffect(() => {
        const requested = requestedVisualTrigger?.current;
        const prevRequested = prevRequestedVisualTrigger?.current;
        if (!activeConfig?.default && requested !== prevRequested) {
            console.log("WARNING: Requested visual trigger", requested);
            prevRequestedVisualTrigger.current = requested;
            setActiveVisualTrigger(requested);
        } else if (activeConfig?.default) {
            setActiveVisualTrigger(null);
        }
    }, [activeConfig, requestedVisualTrigger]);

    const [open, toggleOpen] = useState(false);
    const [messages, threadData, messagesLoading, msgError, banned, handlers] = useMessages({ config: activeConfig, open, currentUser });
    const [chatState, setChatState] = useState(null);

    const isAutoEngageEnabled = activeConfig?.subscription?.addons[1]?.enabled;
    useEffect(() => {
        if (messagesLoading || banned) {
            return;
        }
        if (!activeConfig || !messages || !activeConfig.autoEngages || !chatState) {
            console.log("Null data or chat state.");
            return;
        }
        if (!activeConfig.default) {
            return;
        }
        if (!isAutoEngageEnabled) {
            console.log("Auto engage not enabled.");
            return;
        }
        if (chatState.isDeadEmbed || !chatState.isWithinHours) {
            console.log("Dead embed or outside hours.");
            return;
        }
        for (let i = 0; i < messages.length; i++) {
            if (messages[i].msgType > 0) { // check for user message
                return;
            }
        }
        handlers.handleAutoEngage(activeConfig.autoEngages, open);
    }, [messagesLoading, chatState]);

    useEffect(() => {
        if (messagesLoading || banned) {
            return;
        }
        if (!activeConfig || !messages || !activeConfig?.contactSetting || !activeConfig?.default) {
            return;
        }

        let hasUserMsg = false;

        for (let i = 0; i < messages.length; i += 1) {
            const msgType = messages[i].msgType;
            if (msgType === AUTO_SOLICIT || msgType === AUTO_SOLICIT_SAVED || msgType === AUTO_SOLICIT_CLOSED || msgType === EMAIL_VISITOR) {
                return;
            }
            if (msgType === TEXT_VISITOR || msgType === FILE_VISITOR) {
                hasUserMsg = true;
            }
        }
        if (open && !threadData?.email && hasUserMsg) {
            handlers.addMessage({
                text: "Please enter your email address so we can make sure to get back to you!",
                msgType: AUTO_SOLICIT,
                createdAt: Timestamp.now(),
                seen: open,
            });
        }
    }, [messagesLoading, messages]);

    useEffect(() => {
        if (messagesLoading || !activeConfig) {
            return;
        }
        let isWithinHours = activeConfig.businessHourSetting === 1;
        if (!isWithinHours && activeConfig?.timezone?.offset) {
            const utcDate = new Date();
            const offsetMinutes = activeConfig.timezone.offset * 60;
            const localDate = new Date(utcDate.getTime() + offsetMinutes * 60000);
            
            const day = localDate.getUTCDay();
            const hours = localDate.getUTCHours();
            const minutes = localDate.getUTCMinutes();
        
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const currentDay = activeConfig.businessHours[dayNames[day]];
            const currentMinutes = hours * 60 + minutes;

            if (activeConfig.businessHourSetting === 2) {
                const openMinutes = activeConfig.businessHours.open * 60;
                const closeMinutes = activeConfig.businessHours.close * 60;
                isWithinHours = currentMinutes >= openMinutes && currentMinutes <= closeMinutes;
            } else if (currentDay.isOpen) {
                const openMinutes = currentDay.open * 60;
                const closeMinutes = currentDay.close * 60;
                isWithinHours = currentMinutes >= openMinutes && currentMinutes <= closeMinutes;
            } else {
                isWithinHours = false;
            }
        }
        setChatState({ isWithinHours, isDeadEmbed: false });
    }, [messagesLoading, activeConfig]);

    useEffect(() => {
        if (!activeConfig || !activeConfig.workspaceStyles) {
            console.log("Null config or workspace styles.");
            return;
        }
        // Update the main color based on workspace styles if available
        if (activeConfig?.workspaceStyles?.colors?.name !== prevWorkspaceStyleName.current && activeConfig?.workspaceStyles?.colors?.mainColor) {
            prevWorkspaceStyleName.current = activeConfig?.workspaceStyles?.colors?.name;
            const root = document.getElementById('PageParrotRoot');
            if (root) {
                console.log('setting main color', activeConfig.workspaceStyles.colors.mainColor);
                root.style.setProperty('--main-color', activeConfig.workspaceStyles.colors.mainColor);
                root.style.setProperty('--V-1', activeConfig.workspaceStyles.colors.V1);
                root.style.setProperty('--V-2', activeConfig.workspaceStyles.colors.V2);
                root.style.setProperty('--V-3', activeConfig.workspaceStyles.colors.V3);
                root.style.setProperty('--V-4', activeConfig.workspaceStyles.colors.V4);
                root.style.setProperty('--V-5', activeConfig.workspaceStyles.colors.V5);
            } else {
                console.log('no root');
            }
        }
    }, [activeConfig]);

    useEffect(() => {
        if (activeConfig?.default) {
            toggleOpen(false);
            return;
        }
        if (activeVisualTrigger === 'autoReply') {
            handlers.setMessages([
                {
                    text: chatState?.isWithinHours ? activeConfig?.welcomeMsg : activeConfig?.offlineWelcomeMsg,
                    msgType: MSG_TYPE_ENUM.WELCOME_MSG,
                    createdAt: { seconds: 1, nanoseconds: 0 },
                    seen: true,
                }
            ]);
        } else if (activeVisualTrigger === 'autoEngage') {
            handlers.setMessages([
                {
                    text: activeConfig?.autoEngages[requestedVisualIndex?.current]?.message,
                    msgType: MSG_TYPE_ENUM.AUTO_ENGAGE,
                    createdAt: { seconds: 1, nanoseconds: 0 },
                    seen: true,
                }
            ]);
        } else if (activeVisualTrigger === 'popup') {
            handlers.setMessages([
                {
                    text: "Our hours are 9am to 5pm Monday to Friday.",
                    msgType: MSG_TYPE_ENUM.TEXT_CLIENT,
                    createdAt: { seconds: 1, nanoseconds: 0 },
                    seen: true,
                }
            ]);
        } else {
            let chatMessages = [
                {
                    text: "CHAT DISABLED FOR VISUALIZATION",
                    msgType: MSG_TYPE_ENUM.DISABLED,
                    createdAt: { seconds: 1, nanoseconds: 0 },
                    seen: true,
                },
                {
                    text: chatState?.isWithinHours ? activeConfig?.welcomeMsg : activeConfig?.offlineWelcomeMsg,
                    msgType: MSG_TYPE_ENUM.WELCOME_MSG,
                    createdAt: { seconds: 2, nanoseconds: 0 },
                    seen: true,
                },
                {
                    text: "Hi, what are your hours?",
                    msgType: MSG_TYPE_ENUM.TEXT_VISITOR,
                    createdAt: { seconds: 3, nanoseconds: 0 },
                    seen: true,
                }
            ]
    
            if (activeConfig?.contactSetting) {
                chatMessages.push({
                    text: "Please enter your email address so we can get back to you.",
                    msgType: MSG_TYPE_ENUM.AUTO_SOLICIT,
                    createdAt: { seconds: 4, nanoseconds: 0 },
                    seen: true,
                });
            } else {
                chatMessages.push({
                    text: "Our hours are 9am to 5pm Monday to Friday.",
                    msgType: MSG_TYPE_ENUM.TEXT_CLIENT,
                    createdAt: { seconds: 5, nanoseconds: 0 },
                    seen: true,
                });
            }
            handlers.setMessages(chatMessages);
        }
    }, [activeVisualTrigger, requestedVisualIndex.current, activeConfig]);

    if (msgError) {
        console.log("PageParrot error: Message error");
        return;
    }

    if (!activeConfig?.default && !chatState?.isWithinHours && activeConfig.businessHourSetting !== 1 && !activeConfig?.showOutsideHours) {
        console.log("Outside business hours");
        return;
    }

    const contextValues = {
        name: threadData?.name,
        email: threadData?.email,
        config: activeConfig,
        banned,
        messages,
        messagesLoading,
        msgError,
        open,
        toggleOpen,
        isContactEnabled: activeConfig?.subscription?.addons[3]?.enabled,
        isAutoReplyEnabled: activeConfig?.subscription?.addons[2]?.enabled,
        isAutoEngageEnabled,
        isFileUploadEnabled: activeConfig?.subscription?.addons[0]?.enabled,
        ...chatState,
        ...handlers,

        activeVisualTrigger,
        setActiveVisualTrigger,
    };

    return (      
        <VisualizerContext.Provider value={contextValues}>
            <div id="PageParrotRoot">
                <Chat />
            </div>
        </VisualizerContext.Provider>
    );
}

export function useVisualizerContext() {
    return useContext(VisualizerContext);
}
